import { useSeoMeta } from '@unhead/react';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { isAdminPubkey } from '@/lib/analyticsConfig';
import { track } from '@/lib/analytics';
import {
  SignatureData,
  defaultSignature,
  generateSigId,
  TemplateId,
} from '@/lib/signature';
import { Header } from '@/components/signature/Header';
import { SignatureForm } from '@/components/signature/SignatureForm';
import { TemplatePicker } from '@/components/signature/TemplatePicker';
import { SignaturePreview } from '@/components/signature/SignaturePreview';
import { ConsentBanner } from '@/components/signature/ConsentBanner';
import { BchLogo } from '@/components/signature/BchLogo';
import {
  RotateCcw,
  Sparkles,
  ShieldCheck,
  Zap,
  LayoutTemplate,
  BarChart3,
} from 'lucide-react';

const Index = () => {
  useSeoMeta({
    title: 'BCH Signature — Free Professional Email Signature Maker',
    description:
      'Create a polished, mobile-friendly email signature with your logo, contact details, Bitcoin Cash payment QR, Nostr, and social links. Copy email-ready HTML in one click. Free forever.',
  });

  const { user } = useCurrentUser();
  const isAdmin = isAdminPubkey(user?.pubkey);

  const [data, setData] = useLocalStorage<SignatureData>(
    'bch-signature:data',
    defaultSignature,
    {
      serialize: JSON.stringify,
      // Merge stored data onto defaults so signatures saved before new
      // fields were added are backfilled (prevents undefined-field crashes).
      deserialize: (value) => {
        const stored = JSON.parse(value) as Partial<SignatureData>;
        return {
          ...defaultSignature,
          ...stored,
          social: { ...defaultSignature.social, ...(stored.social ?? {}) },
        };
      },
    },
  );

  // Ensure every signature has a stable anonymous id.
  useEffect(() => {
    if (!data.sigId) {
      setData((prev) => ({ ...prev, sigId: generateSigId() }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createdTracked = useRef(false);

  const patch = (p: Partial<SignatureData>) =>
    setData((prev) => ({ ...prev, ...p }));

  const setTemplate = (template: TemplateId) => patch({ template });

  const handleCopied = () => {
    // Count a "signature created" once per session on first copy.
    if (!createdTracked.current) {
      createdTracked.current = true;
      track('created');
    }
  };

  const reset = () =>
    setData({ ...defaultSignature, sigId: generateSigId() });

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden border-b">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, #0AC18E 0, transparent 40%), radial-gradient(circle at 80% 0%, #0AC18E 0, transparent 35%)',
          }}
        />
        <div className="container relative px-4 py-12 sm:py-16 text-center">
          <div className="mx-auto mb-4 inline-flex items-center gap-2 rounded-full border bg-card px-3 py-1 text-xs font-medium text-muted-foreground shadow-sm">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            Free forever · No sign-up required
          </div>
          <h1 className="mx-auto max-w-2xl text-3xl font-extrabold tracking-tight sm:text-5xl">
            Professional email signatures with{' '}
            <span className="text-primary">Bitcoin Cash</span> built in
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground sm:text-lg">
            Add your details, pick a template, and get email-ready HTML you can
            paste into Gmail, Outlook, Apple Mail and more — complete with a BCH
            payment QR code.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <LayoutTemplate className="h-4 w-4 text-primary" /> 8 templates
            </span>
            <span className="flex items-center gap-1.5">
              <Zap className="h-4 w-4 text-primary" /> One-click copy
            </span>
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4 text-primary" /> Private &
              client-side
            </span>
          </div>
        </div>
      </section>

      {/* Builder */}
      <main className="container px-4 py-8">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
          {/* Left: form */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">Your details</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={reset}
                className="gap-1.5 text-muted-foreground"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Reset
              </Button>
            </div>
            <Card>
              <CardContent className="p-4 sm:p-5">
                <SignatureForm data={data} onChange={patch} />
              </CardContent>
            </Card>
          </div>

          {/* Right: template + preview (sticky on desktop) */}
          <div className="space-y-6">
            <div className="lg:sticky lg:top-20 space-y-6">
              <div className="space-y-3">
                <h2 className="text-lg font-bold">Choose a template</h2>
                <TemplatePicker value={data.template} onChange={setTemplate} />
              </div>

              <div className="space-y-3">
                <h2 className="text-lg font-bold">Preview &amp; export</h2>
                <SignaturePreview data={data} onCopied={handleCopied} />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-muted/30">
        <div className="container flex flex-col items-center justify-between gap-4 px-4 py-8 sm:flex-row">
          <div className="flex items-center gap-2">
            <BchLogo className="h-6 w-6" />
            <span className="text-sm font-semibold">BCH Signature</span>
            <span className="text-xs text-muted-foreground">
              · Peer-to-peer electronic cash
            </span>
          </div>
          <div className="flex items-center gap-4">
            {isAdmin && (
              <Link
                to="/admin"
                className="flex items-center gap-1.5 text-xs font-medium text-primary transition-colors hover:underline"
              >
                <BarChart3 className="h-3.5 w-3.5" /> Analytics
              </Link>
            )}
            <a
              href="https://shakespeare.diy"
              target="_blank"
              rel="noreferrer"
              className="text-xs text-muted-foreground transition-colors hover:text-primary"
            >
              Vibed with Shakespeare
            </a>
          </div>
        </div>
      </footer>

      <ConsentBanner />
    </div>
  );
};

export default Index;
